import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Shield, Users, FileCheck2, IndianRupee, Coins, CheckCircle2, XCircle, Loader2,
  Search, ArrowLeft, Plus, Minus, ExternalLink, RotateCcw, TrendingUp, Cpu, AlertTriangle,
  KeyRound,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const EXAM_LABELS = { upsc: "UPSC", state_psc: "State PSC", generic: "Generic" };

function StatCard({ label, value, Icon, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#525252]">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="font-display text-3xl mt-2">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: ["#EAF1ED", "#2D6A4F", "Paid"],
    created: ["#FBEEE8", "#B0452A", "Created"],
    done: ["#EAF1ED", "#2D6A4F", "Done"],
    processing: ["#EEF3F0", "#2D6A4F", "Processing"],
    failed: ["#FBEEE8", "#C0492B", "Failed"],
    refunded: ["#F3E8EF", "#8A3F6B", "Refunded"],
  };
  const [bg, color, label] = map[status] || ["#F3F3ED", "#525252", status || "—"];
  return <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: bg, color }}>{label}</span>;
}

export default function Admin() {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [payments, setPayments] = useState([]);
  const [paySearch, setPaySearch] = useState("");
  const [payStatus, setPayStatus] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjNote, setAdjNote] = useState("");
  const [adjusting, setAdjusting] = useState(false);

  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [lowMargin, setLowMargin] = useState(null);

  const [credOpen, setCredOpen] = useState(false);
  const [credSaving, setCredSaving] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");

  const openCred = () => {
    setCurPwd("");
    setNewEmail(user?.email || "");
    setNewPwd("");
    setNewPwd2("");
    setCredOpen(true);
  };

  const submitCred = async () => {
    if (!curPwd) return toast.error("Enter your current password to confirm.");
    if (newPwd && newPwd !== newPwd2) return toast.error("New passwords do not match.");
    if (newPwd && newPwd.length < 6) return toast.error("New password must be at least 6 characters.");
    const emailChanged = newEmail && newEmail.toLowerCase() !== (user?.email || "").toLowerCase();
    if (!emailChanged && !newPwd) return toast.error("Change your email or password before saving.");
    setCredSaving(true);
    try {
      const body = { current_password: curPwd };
      if (emailChanged) body.new_email = newEmail;
      if (newPwd) body.new_password = newPwd;
      const { data } = await api.post("/auth/change-credentials", body);
      setUser(data.user);
      toast.success("Admin credentials updated.");
      setCredOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCredSaving(false);
    }
  };

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  const loadLowMargin = useCallback(() => {
    api.get("/admin/low-margin").then(({ data }) => setLowMargin(data)).catch(() => {});
  }, []);
  const loadUsers = useCallback((q = "") => {
    api.get(`/admin/users?search=${encodeURIComponent(q)}`).then(({ data }) => setUsers(data)).catch(() => {});
  }, []);
  const loadPayments = useCallback((q = "", st = "") => {
    api.get(`/admin/payments?search=${encodeURIComponent(q)}&status=${st}`).then(({ data }) => setPayments(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadStats();
    loadUsers();
    loadPayments();
    loadLowMargin();
  }, [loadStats, loadUsers, loadPayments, loadLowMargin]);

  const openUser = async (uid) => {
    setDetail(null);
    setDetailOpen(true);
    setAdjAmount("");
    setAdjNote("");
    try {
      const { data } = await api.get(`/admin/users/${uid}`);
      setDetail(data);
    } catch {
      toast.error("Could not load user");
    }
  };

  const submitAdjust = async () => {
    const amt = parseInt(adjAmount, 10);
    if (!amt || Number.isNaN(amt)) return toast.error("Enter a non-zero amount (e.g. 5 or -3).");
    setAdjusting(true);
    try {
      const { data } = await api.post(`/admin/users/${detail.user.user_id}/credits`, { amount: amt, note: adjNote });
      toast.success(`Credits updated to ${data.credits}`);
      setAdjAmount("");
      setAdjNote("");
      const { data: fresh } = await api.get(`/admin/users/${detail.user.user_id}`);
      setDetail(fresh);
      loadUsers(userSearch);
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAdjusting(false);
    }
  };

  const doRefund = async () => {
    setRefunding(true);
    try {
      const { data } = await api.post(`/admin/payments/${refundTarget.order_id}/refund`);
      toast.success(`Refunded ₹${data.amount_inr}${data.credits != null ? ` · student credits now ${data.credits}` : ""}`);
      setRefundTarget(null);
      loadPayments(paySearch, payStatus);
      loadStats();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDF9]">
      {/* top bar */}
      <header className="sticky top-0 z-40 bg-[#1B2E25] text-white">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#8FCBB0]" />
            <span className="font-display text-xl">MainsMentor Admin</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-white/70">{user?.email}</span>
            <button
              type="button"
              onClick={openCred}
              data-testid="admin-credentials-btn"
              className="inline-flex items-center gap-1.5 text-white/90 hover:text-white"
            >
              <KeyRound className="w-4 h-4" /> Credentials
            </button>
            <Link to="/dashboard" data-testid="admin-exit" className="inline-flex items-center gap-1.5 text-white/90 hover:text-white">
              <ArrowLeft className="w-4 h-4" /> Back to app
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-8">
        <h1 className="font-display text-3xl tracking-tight mb-6">Control Center</h1>

        <Tabs defaultValue="overview">
          <TabsList className="bg-[#EEF3F0]">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">Students</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="margins" data-testid="tab-margins">Margins</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-6">
            {stats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Students" value={stats.students} Icon={Users} accent="#2D6A4F" />
                  <StatCard label="Evaluations" value={stats.total_evaluations} Icon={FileCheck2} accent="#2D6A4F" />
                  <StatCard label="Revenue" value={`₹${stats.revenue_inr}`} Icon={IndianRupee} accent="#D97757" />
                  <StatCard label="Credits Sold" value={stats.credits_sold} Icon={Coins} accent="#D97757" />
                  <StatCard label="Completed" value={stats.done} Icon={CheckCircle2} accent="#2D6A4F" />
                  <StatCard label="Failed" value={stats.failed} Icon={XCircle} accent="#C0492B" />
                  <StatCard label="Avg AI Cost" value={`₹${stats.avg_cost_inr}`} Icon={Cpu} accent="#8A3F6B" />
                  <StatCard label="Total AI Cost" value={`₹${stats.total_cost_inr}`} Icon={Cpu} accent="#8A3F6B" />
                </div>

                {/* Margin readout */}
                <div className="bg-[#EAF1ED] border border-[#CFE3D8] rounded-2xl p-5 flex flex-wrap items-center gap-x-10 gap-y-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-[#2D6A4F] font-semibold">Avg AI cost / evaluation</div>
                    <div className="font-display text-3xl mt-1">₹{stats.avg_cost_inr}</div>
                    <div className="text-xs text-[#525252]">from {stats.costed_evaluations} measured evals</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-[#2D6A4F] font-semibold">Avg tokens (in / out)</div>
                    <div className="font-display text-3xl mt-1">{stats.avg_input_tokens} / {stats.avg_output_tokens}</div>
                  </div>
                  <div className="text-sm text-[#333] max-w-sm">
                    Your price per evaluation is <b>₹9.80</b> (Starter) · <b>₹8.60</b> (Value) · <b>₹7.48</b> (Pro).
                    At an avg cost of <b>₹{stats.avg_cost_inr}</b>, your margin is about <b>₹{(8.6 - stats.avg_cost_inr).toFixed(2)}</b> per evaluation on the Value pack.
                  </div>
                </div>

                {/* Revenue trend */}
                <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-display text-xl tracking-tight">Revenue — last 8 weeks</h3>
                      <p className="text-sm text-[#525252]">Weekly paid revenue (₹)</p>
                    </div>
                    <TrendingUp className="w-5 h-5 text-[#2D6A4F]" />
                  </div>
                  <div className="h-64" style={{ minHeight: 240 }} data-testid="revenue-trend-chart">
                    <ResponsiveContainer width="100%" height="100%" minHeight={240}>
                      <BarChart data={stats.revenue_trend} margin={{ left: -18, right: 8, top: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EEEEE8" vertical={false} />
                        <XAxis dataKey="week" tick={{ fontSize: 12, fill: "#525252" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 12, fill: "#525252" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E5E5", fontSize: 13 }} formatter={(v) => [`₹${v}`, "Revenue"]} cursor={{ fill: "#F3F7F5" }} />
                        <Bar dataKey="revenue" fill="#2D6A4F" radius={[6, 6, 0, 0]} maxBarSize={48} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-[#525252]">Loading…</div>
            )}
          </TabsContent>

          {/* Students */}
          <TabsContent value="students" className="mt-6">
            <div className="flex items-center gap-2 mb-4 max-w-md">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-[#525252] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="user-search"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadUsers(userSearch)}
                  placeholder="Search by name or email"
                  className="pl-9 h-11 rounded-xl"
                />
              </div>
              <Button data-testid="user-search-btn" onClick={() => loadUsers(userSearch)} className="h-11 rounded-xl bg-[#2D6A4F] hover:bg-[#1B4332]">Search</Button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-5 py-3 text-xs uppercase tracking-[0.1em] text-[#525252] border-b border-[#E5E5E5] font-semibold">
                <div className="col-span-4">Student</div>
                <div className="col-span-2 text-center">Credits</div>
                <div className="col-span-2 text-center">Evals</div>
                <div className="col-span-2 text-center">Spent</div>
                <div className="col-span-2 text-right">Action</div>
              </div>
              {users.length === 0 && <div className="px-5 py-8 text-[#525252]">No users found.</div>}
              {users.map((u) => (
                <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-[#F1F1EC] last:border-0 hover:bg-[#FAFAF5]">
                  <div className="col-span-4 min-w-0">
                    <div className="font-medium truncate">{u.name || "—"} {u.role === "admin" && <span className="text-[10px] bg-[#1B2E25] text-white px-1.5 py-0.5 rounded ml-1">ADMIN</span>}</div>
                    <div className="text-sm text-[#525252] truncate">{u.email}</div>
                  </div>
                  <div className="col-span-2 text-center font-semibold text-[#2D6A4F]">{u.credits}</div>
                  <div className="col-span-2 text-center">{u.evaluations_count}</div>
                  <div className="col-span-2 text-center">₹{u.total_spent_inr}</div>
                  <div className="col-span-2 text-right">
                    <Button data-testid={`view-user-${u.user_id}`} variant="outline" size="sm" onClick={() => openUser(u.user_id)} className="rounded-full border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#EAF1ED]">Manage</Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Payments */}
          <TabsContent value="payments" className="mt-6">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative flex-1 min-w-[240px] max-w-md">
                <Search className="w-4 h-4 text-[#525252] absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="payment-search"
                  value={paySearch}
                  onChange={(e) => setPaySearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadPayments(paySearch, payStatus)}
                  placeholder="Search by email, order id or payment id"
                  className="pl-9 h-11 rounded-xl"
                />
              </div>
              {["", "paid", "created"].map((st) => (
                <button
                  key={st || "all"}
                  data-testid={`pay-filter-${st || "all"}`}
                  onClick={() => { setPayStatus(st); loadPayments(paySearch, st); }}
                  className={`px-4 h-11 rounded-xl text-sm font-medium border transition-colors ${payStatus === st ? "bg-[#2D6A4F] text-white border-[#2D6A4F]" : "bg-white text-[#525252] border-[#E5E5E5] hover:bg-[#FAFAF5]"}`}
                >
                  {st === "" ? "All" : st === "paid" ? "Paid" : "Created"}
                </button>
              ))}
              <Button data-testid="payment-search-btn" onClick={() => loadPayments(paySearch, payStatus)} className="h-11 rounded-xl bg-[#2D6A4F] hover:bg-[#1B4332]">Search</Button>
            </div>

            <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-x-auto">
              <div className="min-w-[820px]">
                <div className="grid grid-cols-12 gap-2 px-5 py-3 text-xs uppercase tracking-[0.1em] text-[#525252] border-b border-[#E5E5E5] font-semibold">
                  <div className="col-span-3">Student</div>
                  <div className="col-span-3">Order / Payment ID</div>
                  <div className="col-span-2 text-center">Amount</div>
                  <div className="col-span-2 text-center">Status</div>
                  <div className="col-span-2 text-right">Date</div>
                </div>
                {payments.length === 0 && <div className="px-5 py-8 text-[#525252]">No payments found.</div>}
                {payments.map((p) => (
                  <div key={p.order_id} data-testid={`payment-row-${p.order_id}`} className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-[#F1F1EC] last:border-0 hover:bg-[#FAFAF5]">
                    <div className="col-span-3 min-w-0">
                      <div className="font-medium truncate text-sm">{p.user_name || "—"}</div>
                      <div className="text-xs text-[#525252] truncate">{p.user_email}</div>
                    </div>
                    <div className="col-span-3 min-w-0 text-xs font-mono">
                      <div className="truncate text-[#333]">{p.order_id}</div>
                      <div className="truncate text-[#525252]">{p.payment_id || "—"}</div>
                    </div>
                    <div className="col-span-2 text-center font-semibold">₹{(p.amount / 100).toFixed(0)}<span className="text-xs text-[#525252] font-normal"> · {p.credits}cr</span></div>
                    <div className="col-span-2 text-center"><StatusPill status={p.status} /></div>
                    <div className="col-span-2 text-right">
                      <div className="text-xs text-[#525252]">{new Date(p.created_at).toLocaleString()}</div>
                      {p.status === "paid" && (
                        <button
                          data-testid={`refund-${p.order_id}`}
                          onClick={() => setRefundTarget(p)}
                          className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#C0492B] hover:underline"
                        >
                          <RotateCcw className="w-3 h-3" /> Refund
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-[#525252] mt-3">Tip: a payment stuck on "Created" means the student started checkout but it wasn't confirmed. If they say money was deducted, verify in your Razorpay dashboard, then add the credits to their account from the Students tab.</p>
          </TabsContent>

          {/* Margins */}
          <TabsContent value="margins" className="mt-6">
            {!lowMargin ? (
              <div className="text-[#525252]">Loading…</div>
            ) : (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5 flex flex-wrap items-center gap-x-10 gap-y-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-[#525252] font-semibold">Flagged evaluations</div>
                    <div className="font-display text-3xl mt-1" style={{ color: lowMargin.count > 0 ? "#C0492B" : "#2D6A4F" }} data-testid="low-margin-count">{lowMargin.count}</div>
                  </div>
                  <div className="text-sm text-[#333] max-w-xl">
                    An evaluation is flagged when its AI cost is at least <b>{Math.round(lowMargin.ratio * 100)}%</b> of your cheapest per-evaluation price
                    (<b>₹{lowMargin.benchmark_price_inr}</b>, the tightest-margin pack) — i.e. cost ≥ <b>₹{lowMargin.threshold_cost_inr}</b>.
                    <span className="block text-xs text-[#525252] mt-1">Per-eval prices: {lowMargin.packs.map((p) => `${p.name} ₹${p.per_eval}`).join(" · ")}. Tune the threshold via LOW_MARGIN_RATIO.</span>
                  </div>
                </div>

                {lowMargin.count === 0 ? (
                  <div className="bg-[#EAF1ED] border border-[#CFE3D8] rounded-2xl p-10 text-center">
                    <CheckCircle2 className="w-9 h-9 text-[#2D6A4F] mx-auto mb-3" />
                    <p className="font-semibold text-lg">All evaluations are healthily profitable</p>
                    <p className="text-sm text-[#525252] mt-1">No evaluation has crossed the low-margin threshold. Nice.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-x-auto">
                    <div className="min-w-[760px]">
                      <div className="grid grid-cols-12 gap-2 px-5 py-3 text-xs uppercase tracking-[0.1em] text-[#525252] border-b border-[#E5E5E5] font-semibold">
                        <div className="col-span-5">Question</div>
                        <div className="col-span-3">Student</div>
                        <div className="col-span-1 text-center">Pages</div>
                        <div className="col-span-1 text-center">Cost</div>
                        <div className="col-span-2 text-right">Cost vs price</div>
                      </div>
                      {lowMargin.items.map((it) => (
                        <Link key={it.id} to={`/result/${it.id}`} target="_blank" data-testid={`lowmargin-row-${it.id}`} className="grid grid-cols-12 gap-2 px-5 py-3 items-center border-b border-[#F1F1EC] last:border-0 hover:bg-[#FAFAF5]">
                          <div className="col-span-5 min-w-0">
                            <span className="text-[10px] uppercase font-semibold text-[#2D6A4F] mr-1">{EXAM_LABELS[it.exam_type] || it.exam_type}</span>
                            <span className="truncate">{it.question}</span>
                          </div>
                          <div className="col-span-3 truncate text-sm text-[#525252]">{it.user_email}</div>
                          <div className="col-span-1 text-center">{it.page_count}</div>
                          <div className="col-span-1 text-center font-semibold text-[#8A3F6B]">₹{it.cost_inr}</div>
                          <div className="col-span-2 text-right">
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1" style={{ background: it.is_loss ? "#FBE3DC" : "#FBEFE3", color: it.is_loss ? "#C0492B" : "#B0452A" }}>
                              {it.is_loss && <AlertTriangle className="w-3 h-3" />}{Math.round(it.ratio * 100)}%{it.is_loss ? " · LOSS" : ""}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Student detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Student details</DialogTitle>
          </DialogHeader>
          {!detail ? (
            <div className="py-10 text-center text-[#525252]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between bg-[#FAFAF5] rounded-xl p-4">
                <div>
                  <div className="font-semibold">{detail.user.name || "—"}</div>
                  <div className="text-sm text-[#525252]">{detail.user.email}</div>
                  <div className="text-xs text-[#525252] mt-1">Provider: {detail.user.auth_provider} · Joined {new Date(detail.user.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#525252]">Credits</div>
                  <div className="font-display text-3xl text-[#2D6A4F]">{detail.user.credits}</div>
                </div>
              </div>

              {/* Adjust credits */}
              <div className="border border-[#E5E5E5] rounded-xl p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Coins className="w-4 h-4 text-[#D97757]" /> Adjust credits</h4>
                <div className="flex items-end gap-2">
                  <div className="flex items-center gap-1">
                    <button type="button" data-testid="adj-minus" onClick={() => setAdjAmount(String((parseInt(adjAmount || "0", 10) || 0) - 1))} className="w-9 h-9 rounded-lg border border-[#E5E5E5] flex items-center justify-center hover:bg-[#FAFAF5]"><Minus className="w-4 h-4" /></button>
                    <Input data-testid="adj-amount" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} placeholder="e.g. 5" className="w-20 h-9 text-center rounded-lg" />
                    <button type="button" data-testid="adj-plus" onClick={() => setAdjAmount(String((parseInt(adjAmount || "0", 10) || 0) + 1))} className="w-9 h-9 rounded-lg border border-[#E5E5E5] flex items-center justify-center hover:bg-[#FAFAF5]"><Plus className="w-4 h-4" /></button>
                  </div>
                  <Input data-testid="adj-note" value={adjNote} onChange={(e) => setAdjNote(e.target.value)} placeholder="Reason (e.g. payment stuck, order_xxx)" className="flex-1 h-9 rounded-lg" />
                  <Button data-testid="adj-submit" onClick={submitAdjust} disabled={adjusting} className="h-9 rounded-lg bg-[#2D6A4F] hover:bg-[#1B4332]">{adjusting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}</Button>
                </div>
                <p className="text-xs text-[#525252] mt-2">Use a negative number to deduct. Every change is logged below.</p>
              </div>

              {/* Payments */}
              <div>
                <h4 className="font-semibold mb-2">Payments ({detail.payments.length})</h4>
                {detail.payments.length === 0 ? <p className="text-sm text-[#525252]">No payments.</p> : (
                  <div className="space-y-2">
                    {detail.payments.map((p) => (
                      <div key={p.order_id} className="flex items-center justify-between text-sm bg-[#FAFAF5] rounded-lg px-3 py-2">
                        <div className="font-mono text-xs truncate mr-2">{p.order_id}</div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span>₹{(p.amount / 100).toFixed(0)} · {p.credits}cr</span>
                          <StatusPill status={p.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Evaluations */}
              <div>
                <h4 className="font-semibold mb-2">Evaluations ({detail.evaluations.length})</h4>
                {detail.evaluations.length === 0 ? <p className="text-sm text-[#525252]">No evaluations.</p> : (
                  <div className="space-y-2">
                    {detail.evaluations.slice(0, 25).map((e) => (
                      <Link key={e.id} to={`/result/${e.id}`} target="_blank" className="flex items-center justify-between text-sm bg-[#FAFAF5] rounded-lg px-3 py-2 hover:bg-[#F1F1EC]">
                        <div className="min-w-0 mr-2">
                          <span className="text-[10px] uppercase font-semibold text-[#2D6A4F] mr-1">{EXAM_LABELS[e.exam_type] || e.exam_type}</span>
                          <span className="truncate">{e.question}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {e.usage?.cost_inr != null && (
                            <span className="text-[11px] inline-flex items-center gap-0.5" style={{ color: stats && e.usage.cost_inr >= stats.low_margin_threshold_inr ? "#C0492B" : "#8A3F6B" }}>
                              {stats && e.usage.cost_inr >= stats.low_margin_threshold_inr && <AlertTriangle className="w-3 h-3" />}₹{e.usage.cost_inr.toFixed(2)}
                            </span>
                          )}
                          {e.result ? <span className="font-semibold text-[#2D6A4F]">{e.result.score}/{e.result.max_marks}</span> : <StatusPill status={e.status} />}
                          <ExternalLink className="w-3.5 h-3.5 text-[#525252]" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Adjustment log */}
              {detail.adjustments.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Credit adjustment log</h4>
                  <div className="space-y-1.5">
                    {detail.adjustments.map((a) => (
                      <div key={a.id} className="text-xs text-[#525252] flex items-center justify-between bg-[#FAFAF5] rounded-lg px-3 py-2">
                        <span>{a.amount > 0 ? "+" : ""}{a.amount} cr — {a.note || "no note"} <span className="text-[#999]">by {a.admin_email}</span></span>
                        <span>{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund confirmation dialog */}
      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent className="max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Refund payment?</DialogTitle>
          </DialogHeader>
          {refundTarget && (
            <div className="space-y-4">
              <p className="text-[#525252]">
                This will refund <b>₹{(refundTarget.amount / 100).toFixed(0)}</b> to <b>{refundTarget.user_email}</b> via Razorpay and
                remove the <b>{refundTarget.credits} credits</b> that were granted (their balance won't go below 0).
              </p>
              <div className="bg-[#FAFAF5] rounded-lg p-3 text-xs font-mono break-all">
                <div>order: {refundTarget.order_id}</div>
                <div>payment: {refundTarget.payment_id || "—"}</div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setRefundTarget(null)} className="rounded-full">Cancel</Button>
                <Button data-testid="confirm-refund-btn" onClick={doRefund} disabled={refunding} className="rounded-full bg-[#C0492B] hover:bg-[#a13d24] text-white">
                  {refunding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refund now"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change credentials dialog */}
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent className="max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#2D6A4F]" /> Admin login &amp; password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#525252]">
              Update the email and password you use to sign in to the admin portal. Leave a field unchanged to keep it.
              You'll stay signed in on this device; any other sessions will be logged out.
            </p>
            <div>
              <Label htmlFor="cred-cur">Current password</Label>
              <Input id="cred-cur" type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)}
                     data-testid="cred-current-password" placeholder="Enter current password" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="cred-email">New login email</Label>
              <Input id="cred-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                     data-testid="cred-new-email" placeholder="admin@mainsmentor.in" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cred-pwd">New password</Label>
                <Input id="cred-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                       data-testid="cred-new-password" placeholder="Min 6 characters" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="cred-pwd2">Confirm password</Label>
                <Input id="cred-pwd2" type="password" value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)}
                       data-testid="cred-confirm-password" placeholder="Repeat password" className="mt-1" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setCredOpen(false)} className="rounded-full">Cancel</Button>
              <Button data-testid="save-credentials-btn" onClick={submitCred} disabled={credSaving}
                      className="rounded-full bg-[#2D6A4F] hover:bg-[#255a43] text-white">
                {credSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
